import type { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import type { IHighlightedTextArea } from '@pushly/aqe/lib/components/highlighted-text-area/highlighted-text-area'
import { bindingById } from '@pushly/models/lib/types'
import { BRAND_VOICE_TIER_VISUAL } from './brand-voice-tier-visual'
import { locateSpan } from './span-locator'

/** The shape aqe's HighlightedTextArea consumes. Taken FROM the component rather than restated
 * here, so a widening on its side (as `dimmedBackground` was) cannot fail to reach these maps. */
type TierStyle = IHighlightedTextArea['tierStyles'][string]

export type HighlightTier = 'checked' | 'needsReview'

/** All four compiled-directive tiers, including the two (`guaranteed`, `guidance`) that never
 * get per-item hover/click wiring in the editor drawer's decomposition rows — the display well
 * (see `findAllTierSpans` below) highlights all four, so it needs styles for all four. */
export type AnyHighlightTier = HighlightTier | 'guaranteed' | 'guidance'

export interface HighlightSpan {
    key: string
    tier: HighlightTier
    start: number
    end: number
    /** The body text this span covered when it was compiled. Carried so the span can be checked
     * against — and re-found in — text the author has edited since. See {@link reconcileSpans}. */
    quote: string
}

/** Key for the Nth Guidance statement. `style` is now an array of independently-extracted
 * statements (it was one synthesized paragraph, which is what made the tier unstable), so each
 * gets its own key exactly like `assertions`/`unmapped`. */
export const guidanceSpanKey = (i: number) => `guidance-${i}`

export interface AnyHighlightSpan {
    key: string
    tier: AnyHighlightTier
    start: number
    end: number
    /** See {@link HighlightSpan.quote}. */
    quote: string
}

/**
 * aqe's `HighlightedTextArea` is tier-agnostic — it takes a plain `Record<tier, {background,
 * activeColor}>` rather than importing anything Brand-Voice-specific. This is that mapping for
 * Brand Voice's two linkable tiers, derived from the same `BRAND_VOICE_TIER_VISUAL` the rule
 * rows themselves render from, so the textarea and the rows can never visually disagree.
 *
 * BACKGROUND ALPHA is where highlight density is controlled, and the only place it should be.
 * Held at 25% in light mode deliberately: a fully-analyzed directive marks nearly every line, and
 * softening it was tried and reverted — the dense marking is the signal.
 *
 * DARK MODE NEEDS A DIFFERENT NUMBER, not the same one. 25% of a tier colour over a near-black
 * ground is a murky band that sits BELOW the page in luminance, so it darkens the text it is
 * meant to be lifting and reads as a smudge. The dark values let more of the colour through so
 * the mark rises off the ground the way a highlight is supposed to.
 *
 * Which is why these are CSS variables rather than colours computed here: the styles are handed
 * to `HighlightedTextArea` as inline `backgroundColor`, and an inline style cannot answer a
 * theme change. The variables are defined in brand-voice-decomposition.scss against
 * `[data-theme]`, which aqe's ThemeModeProvider stamps on <html> — and which also honours an
 * explicit user choice, where `prefers-color-scheme` alone would not.
 *
 * Never solve density by fading the marks themselves. `opacity` on a `<mark>` fades its TEXT
 * along with its background, so the author's own words go pale simply for having produced a rule.
 * The words are the content; the highlight is the annotation.
 */
export const HIGHLIGHT_TIER_STYLES: Record<HighlightTier, TierStyle> = {
    checked: {
        background: `var(--sw-bv-wash-checked)`,
        dimmedBackground: `var(--sw-bv-dim-checked)`,
        activeColor: BRAND_VOICE_TIER_VISUAL.checked.color,
    },
    needsReview: {
        background: `var(--sw-bv-wash-needsReview)`,
        dimmedBackground: `var(--sw-bv-dim-needsReview)`,
        activeColor: BRAND_VOICE_TIER_VISUAL.needsReview.color,
    },
}

/** Same idea as `HIGHLIGHT_TIER_STYLES`, extended to all four tiers for the read-only display
 * well — see `AnyHighlightTier`. */
export const ALL_TIER_HIGHLIGHT_STYLES: Record<AnyHighlightTier, TierStyle> = {
    guaranteed: {
        background: `var(--sw-bv-wash-guaranteed)`,
        dimmedBackground: `var(--sw-bv-dim-guaranteed)`,
        activeColor: BRAND_VOICE_TIER_VISUAL.guaranteed.color,
    },
    ...HIGHLIGHT_TIER_STYLES,
    guidance: {
        background: `var(--sw-bv-wash-guidance)`,
        dimmedBackground: `var(--sw-bv-dim-guidance)`,
        activeColor: BRAND_VOICE_TIER_VISUAL.guidance.color,
    },
}

/**
 * Builds the textarea<->rule highlight spans directly from a compiled directive's own
 * attribution (NS-2668) — each Checked/Needs-review item's `sources` is resolved server-side,
 * at compile time, against the exact body that produced it (see sealab-ts's
 * `resolveSources`/`source-attribution.ts`). This replaces the earlier client-side, post-hoc
 * string search entirely: that search could never resolve a genuine paraphrase (there was no
 * verbatim substring left to find), and the compiler is the one place that actually knows which
 * input text produced which rule.
 *
 * An item with an empty `sources` array is a normal, expected outcome — a paraphrase, or a
 * synthesized fact with nothing to quote — not a failure. It simply contributes no span, and
 * its row stays non-interactive (see `matchedSpanKeys` in brand-voice-decomposition.tsx).
 *
 * One item can resolve to more than one span (see `AttributedText`'s own docstring, e.g. a rule
 * merging two separate sentences) — each becomes its own `HighlightSpan` sharing that item's
 * key, so hovering/matching still works per-item rather than per-span.
 */
export function findVerbatimSpans(compiled: Pick<CompiledDirective, 'assertions' | 'unmapped'>): HighlightSpan[] {
    const spans: HighlightSpan[] = []

    ;(compiled.assertions ?? []).forEach((item, i) => {
        ;(item.sources ?? []).forEach((source) => {
            spans.push({
                key: `checked-${i}`,
                tier: 'checked',
                start: source.start,
                end: source.end,
                quote: source.quote,
            })
        })
    })
    ;(compiled.unmapped ?? []).forEach((item, i) => {
        ;(item.sources ?? []).forEach((source) => {
            spans.push({
                key: `needsReview-${i}`,
                tier: 'needsReview',
                start: source.start,
                end: source.end,
                quote: source.quote,
            })
        })
    })

    return spans.sort((a, b) => a.start - b.start)
}

/**
 * Same idea as `findVerbatimSpans`, extended to all four tiers — for the read-only display well
 * (`BrandVoiceWell`), which highlights everything a directive compiled into, not just the two
 * tiers the editor drawer links to rows. `resolved` (Guaranteed) and `style` (Guidance) carry
 * `sources` the same way `assertions`/`unmapped` do; nothing new server-side was needed.
 *
 * Kept separate from `findVerbatimSpans` rather than widening it in place — the editor drawer's
 * decomposition rows only ever wire hover/click for Checked/Needs-review, so handing it
 * Guaranteed/Guidance spans it has no matching interactive rows for would just add
 * non-interactive marks to that view with no corresponding change requested there.
 */
export function findAllTierSpans(
    compiled: Pick<CompiledDirective, 'resolved' | 'assertions' | 'style' | 'unmapped'>,
): AnyHighlightSpan[] {
    const spans: AnyHighlightSpan[] = []

    for (const row of guaranteedDisplayRows(compiled)) {
        for (const source of row.sources) {
            spans.push({
                key: row.key,
                tier: 'guaranteed',
                start: source.start,
                end: source.end,
                quote: source.quote,
            })
        }
    }
    ;(compiled.assertions ?? []).forEach((item, i) => {
        ;(item.sources ?? []).forEach((source) => {
            spans.push({
                key: `checked-${i}`,
                tier: 'checked',
                start: source.start,
                end: source.end,
                quote: source.quote,
            })
        })
    })
    ;(compiled.style ?? []).forEach((item, i) => {
        ;(item.sources ?? []).forEach((source) => {
            spans.push({
                key: guidanceSpanKey(i),
                tier: 'guidance',
                start: source.start,
                end: source.end,
                quote: source.quote,
            })
        })
    })
    ;(compiled.unmapped ?? []).forEach((item, i) => {
        ;(item.sources ?? []).forEach((source) => {
            spans.push({
                key: `needsReview-${i}`,
                tier: 'needsReview',
                start: source.start,
                end: source.end,
                quote: source.quote,
            })
        })
    })

    // Deliberately NOT deduped: this list also answers "which items have attribution", via
    // `matchedSpanKeys`. Collapsing duplicate ranges here would drop the keys of every entry
    // after the first in a fan-out. See `dedupeSpansForRender`, applied at the render boundary.
    return spans.sort((a, b) => a.start - b.start)
}

/**
 * Drops or re-places spans so they always describe the text CURRENTLY on screen.
 *
 * Spans are offsets into the body that was compiled, and the author keeps typing. Nothing errors
 * when those diverge — the numbers stay valid, they just stop meaning anything — so the marks
 * quietly slide out of register: half a word highlighted here, a phantom range there. Replacing
 * the whole directive with different text is the extreme case, where every mark lands somewhere
 * arbitrary; a single line typed above the first rule is the everyday one.
 *
 * Reconciling against the quote is what makes this exact rather than a guess. An edit AFTER a
 * span leaves it verified in place; an edit before or around it shifts it by a recoverable
 * amount; an edit THROUGH it destroys the quote, and the span is dropped — which is right,
 * because a rule whose source sentence has been rewritten no longer has a source to point at.
 *
 * Dropping also correctly de-wires the row: `matchedSpanKeys` is built from this list, so a rule
 * whose text is gone stops offering a hover/click that would highlight nothing.
 */
export function reconcileSpans<T extends { start: number; end: number; quote: string }>(
    spans: T[],
    body: string,
    compiledBody?: string,
): T[] {
    const reconciled: T[] = []

    for (const span of spans) {
        const located = locateSpan(body, span, compiledBody)
        if (located) reconciled.push({ ...span, ...located })
    }

    return reconciled.sort((a, b) => a.start - b.start)
}

/**
 * Drops spans that would paint the exact same range twice within a tier — for RENDERING only.
 *
 * Kept separate from {@link findAllTierSpans} deliberately. That function is also the source of
 * `matchedSpanKeys`, i.e. "which items have attribution at all", and deduping there silently
 * dropped the keys of every entry after the first in a fan-out: a directive banning three words
 * produces three `resolved` entries that all quote the SAME sentence, so two of the three rows
 * lost their wiring despite being perfectly well attributed. Identical ranges still need to
 * collapse before rendering, because HighlightedTextArea walks a single monotonic cursor and
 * silently skips anything already consumed — but that is a painting concern, not an
 * attribution one.
 */
export function dedupeSpansForRender(spans: AnyHighlightSpan[]): AnyHighlightSpan[] {
    const seen = new Set<string>()
    return spans.filter((s) => {
        const id = `${s.tier}:${s.start}:${s.end}`
        if (seen.has(id)) return false
        seen.add(id)
        return true
    })
}

/**
 * One entry per row the Guaranteed section should SHOW, which is not one per PQuery condition.
 *
 * `banned_terms` is a single `string_array` binding, and the compiler projects it by fanning out
 * to one `message.raw does not contain X` condition per term — a consequence of
 * `CONTENT_DIRECTIVE_PROFILE` being depth-1 and AND-only, not a modelling decision. Rendered
 * literally that produced nine near-identical rows for what the author experienced as two or
 * three sentences, and it pushed every other tier below the fold. Collapsing them back into one
 * row with a term list is more faithful to the compiled model, not less: `resolved` holds one
 * binding, and this restores it.
 *
 * Grouping is by binding id, and only for `string_array` — an integer limit or a flag is one
 * rule per entry already, and merging two length limits would be a lie.
 *
 * SOURCES ARE THE ROW'S, NOT THE TERM'S. Every fanned-out entry carries the same source list
 * (the compiler attributes the BINDING to every sentence that contributed a term), so a row's
 * sources are simply those sentences. This replaces an earlier per-term narrowing that existed
 * only to give the duplicate rows distinguishable spans: with one row there is nothing to
 * distinguish, and highlighting the sentences that stated the rule beats highlighting individual
 * characters scattered through them. It also drops that narrowing's worst behavior — a term not
 * found inside a given quote fell back to the WHOLE sentence, so the rule for "!" claimed the
 * sentence about "=" as its source.
 */
export interface GuaranteedDisplayRow {
    /** Span key shared by every entry folded into this row. */
    key: string
    attribute: string
    operator: string
    /** An array when several terms were folded together, the entry's own value otherwise. */
    value: unknown
    sources: { start: number; end: number; quote: string }[]
}

export function guaranteedDisplayRows(compiled: Pick<CompiledDirective, 'resolved'>): GuaranteedDisplayRow[] {
    const rows: GuaranteedDisplayRow[] = []
    const groupedByBindingId = new Map<string, GuaranteedDisplayRow>()

    ;(compiled.resolved ?? []).forEach((item, i) => {
        const binding = item.binding ? bindingById(item.binding.id) : undefined
        if (!binding) return

        const sources = (item.sources ?? []).map((s) => ({ start: s.start, end: s.end, quote: s.quote }))

        if (binding.value.kind !== 'string_array') {
            rows.push({
                key: `guaranteed-${i}`,
                attribute: binding.attribute,
                operator: binding.operator,
                value: item.binding.value,
                sources,
            })
            return
        }

        const existing = groupedByBindingId.get(binding.id)
        if (existing) {
            ;(existing.value as unknown[]).push(item.binding.value)
            // Sources repeat across the fan-out; keep one of each so a row does not paint the
            // same sentence several times over.
            for (const source of sources) {
                if (!existing.sources.some((s) => s.start === source.start && s.end === source.end)) {
                    existing.sources.push(source)
                }
            }
            return
        }

        // Keyed on the FIRST entry's index, so keys keep the same shape as every other tier's
        // and stay stable for a given compile.
        const row: GuaranteedDisplayRow = {
            key: `guaranteed-${i}`,
            attribute: binding.attribute,
            operator: binding.operator,
            value: [item.binding.value],
            sources,
        }
        groupedByBindingId.set(binding.id, row)
        rows.push(row)
    })

    // Term sources are filtered once the row's full value list is known — see
    // `sourcesStatingATerm`.
    for (const row of groupedByBindingId.values()) {
        row.sources = sourcesStatingATerm(row.sources, row.value as unknown[])
    }

    return rows
}

/**
 * Keeps only the sentences that actually state one of the terms.
 *
 * The compiler attributes a `string_array` BINDING to every sentence it believes contributed,
 * and it over-reaches: `"www" should not be place before a web address` is do-not-use shaped, so
 * it gets listed as a banned-terms source despite naming no banned term. Measured in a real
 * directive, and the consequence was not cosmetic — that sentence is ALSO an assertion's source,
 * and `HighlightedTextArea` walks a single monotonic cursor, so the guaranteed span (pushed
 * first) painted the sentence green and the Checked span covering the same range was skipped
 * entirely. The row stayed in Checked, its mark never rendered, and hovering it lit nothing:
 * exactly the "interaction doesn't work" symptom, with no error anywhere.
 *
 * This is the precise half of what `narrowToLiteralValue` used to attempt. That function tried to
 * point at the literal WITHIN each quote and fell back to the whole sentence when it wasn't
 * there — which is how an unrelated sentence became a full-width highlight. Dropping the source
 * is the right answer to "this sentence contains none of these terms": we do not know that it
 * contributed, so we do not claim it.
 *
 * Case-insensitive, because the author's casing and the extracted term need not agree
 * ("Cutting-Edge" in prose, `cutting-edge` as the term).
 *
 * Returning EMPTY is a legitimate outcome — the row simply has no attribution and its PQuery row
 * stays non-interactive. Falling back to every quote would restore the over-claim this exists to
 * remove.
 */
function sourcesStatingATerm(
    sources: { start: number; end: number; quote: string }[],
    terms: unknown[],
): { start: number; end: number; quote: string }[] {
    const literals = terms.filter((t): t is string => typeof t === 'string' && t.length > 0).map((t) => t.toLowerCase())
    if (literals.length === 0) return sources

    return sources.filter((source) => {
        const quote = source.quote.toLowerCase()
        return literals.some((term) => quote.includes(term))
    })
}

/**
 * The condition group to hand PQueryBuilder — the collapsed rows, not the raw `spec`.
 *
 * Display only. The persisted `spec` is what actually gets enforced and is never touched: this
 * is the same set of rules with the fan-out folded back up for reading.
 *
 * An empty set is `{}` rather than `{ and: [] }`, matching what the compiler stores — the PQuery
 * reader rejects empty groups.
 */
export function collapsedGuaranteedSpec(compiled: Pick<CompiledDirective, 'resolved'>): Record<string, unknown> {
    const conditions = guaranteedDisplayRows(compiled).map((row) => ({
        [row.attribute]: { [row.operator]: row.value },
    }))

    return conditions.length > 0 ? { and: conditions } : {}
}

/**
 * Maps each Guaranteed condition, by its PQuery identity, to the span key of the display row
 * that produced it — so a rendered PQueryBuilder row can be linked back to the author's text.
 *
 * Keyed on `{property, operator, value}` rather than position, deliberately: the builder can
 * only read a condition off what it rendered, and the identity is what both sides can compute
 * independently. Derived from {@link guaranteedDisplayRows} so the grouping used to BUILD the
 * spec and the grouping used to LINK it can never disagree.
 */
export function guaranteedRowKeyIndex(compiled: Pick<CompiledDirective, 'resolved'>): Map<string, string> {
    const index = new Map<string, string>()
    for (const row of guaranteedDisplayRows(compiled)) {
        index.set(conditionIdentity(row.attribute, row.operator, row.value), row.key)
    }
    return index
}

/** Stable string form of a condition's identity — see {@link guaranteedRowKeyIndex}. */
export function conditionIdentity(property: string, operator: string, value: unknown): string {
    return `${property}|${operator}|${JSON.stringify(value ?? null)}`
}
